/**
 * Kenttavirhe.tsx — lomakkeen virheilmoitus kentän kohdalla (ydin, T677 havainto J; Toni 27.10.2026 klo 09.12).
 *
 * Sääntö: virheilmoitus näkyy punaisena kentän alla tai vieressä, kenttä saa punaisen reunuksen (aria-invalid), kenttään siirrytään
 * (focus), ja ilmoitus kertoo mitä tehdä («Kirjoita peruste: …»). Harmaa ohjeteksti on eri asia kuin virhe. Ennen tätä lomakkeet
 * näyttivät kiellon pienenä harmaana tekstinä lomakkeen tai taulukon yläpuolella, kaukana kentästä (punch-lomake «Kuvaa vika.»,
 * kiristyksen palautus ilman perustetta), eikä sitä huomannut.
 *
 * Käyttö:
 *   const v = useKenttavirheet<"vika" | "luokka">();
 *   <input {...v.kentta("vika")} … />  <Kenttavirhe {...v.virhe("vika")} />
 *   if (!vika.trim()) return v.aseta("vika", t("…"));      // asettaa, näyttää ja siirtää kohdistuksen kenttään
 *   v.tyhjenna();                                          // ennen uutta yritystä tai onnistuneen tallennuksen jälkeen
 * Palvelimen nimetty virhe kohdistetaan kenttään samalla aseta()-kutsulla; kenttään sitomaton virhe näytetään lomakkeen omalla
 * paikalla (role="alert") kuten ennenkin — vain kentän puute kuuluu kentän kohdalle.
 */
import { useCallback, useRef, useState, type ReactElement } from "react";

export interface KenttavirheProps { id: string; viesti: string | null }

/** Punainen virherivi kentän alla. Tyhjä viesti ei piirrä mitään (ei varattua tilaa). */
export function Kenttavirhe({ id, viesti }: KenttavirheProps): ReactElement | null {
  if (!viesti) return null;
  return <p className="kenttavirhe" role="alert" id={id} data-kenttavirhe="">{viesti}</p>;
}

type Kohdistettava = HTMLElement & { focus: () => void };

export function useKenttavirheet<K extends string>() {
  const [virheet, setVirheet] = useState<Partial<Record<K, string>>>({});
  const viitteet = useRef<Partial<Record<K, Kohdistettava | null>>>({});
  const etuliite = useRef(`kv-${Math.random().toString(36).slice(2, 8)}`).current;

  /** Asettaa kentän virheen ja siirtää kohdistuksen kenttään. Palauttaa false, jotta `return v.aseta(…)` katkaisee tallennuksen. */
  const aseta = useCallback((kentta: K, viesti: string): false => {
    setVirheet((v) => ({ ...v, [kentta]: viesti }));
    const el = viitteet.current[kentta];
    if (el) { el.focus(); el.scrollIntoView?.({ block: "center", behavior: "smooth" }); }
    return false;
  }, []);
  /** Lomakkeen tarkistus kerralla: jokainen [kenttä, viesti | false] — kaikki puutteet näkyviin, kohdistus ensimmäiseen. true = kunnossa. */
  const tarkista = useCallback((ehdot: [K, string | false | null | undefined][]): boolean => {
    const puuttuu = ehdot.filter((e): e is [K, string] => !!e[1]);
    setVirheet(Object.fromEntries(puuttuu) as Partial<Record<K, string>>);
    const eka = puuttuu[0] ? viitteet.current[puuttuu[0][0]] : null;
    if (eka) { eka.focus(); eka.scrollIntoView?.({ block: "center", behavior: "smooth" }); }
    return puuttuu.length === 0;
  }, []);
  const poista = useCallback((kentta: K) => setVirheet((v) => {
    if (!(kentta in v)) return v;
    const u = { ...v }; delete u[kentta]; return u;
  }), []);
  const tyhjenna = useCallback(() => setVirheet({}), []);

  /** Kentän ominaisuudet: viite kohdistusta varten, aria-invalid (punainen reunus) ja aria-describedby virheriviin. */
  const kentta = (k: K) => ({
    ref: (el: Kohdistettava | null) => { viitteet.current[k] = el; },
    "aria-invalid": virheet[k] ? true : undefined,
    "aria-describedby": virheet[k] ? `${etuliite}-${k}` : undefined,
    "data-kentta-virheellinen": virheet[k] ? "" : undefined,
  });
  const virhe = (k: K): KenttavirheProps => ({ id: `${etuliite}-${k}`, viesti: virheet[k] ?? null });

  return { aseta, tarkista, poista, tyhjenna, kentta, virhe, onVirheita: Object.keys(virheet).length > 0 };
}
